import { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { KeyRound, Mail } from 'lucide-react'
import { Alert, Button, Input } from '@/components/ui'
import { api, ApiError, USE_MOCKS, beginSso } from '@/internationalization/api-client'
import { useT } from '@/internationalization/i18n'
import { useSessionStore } from '@/stores'
import type { Tenant, User } from '@/types'

/**
 * Sign in.
 *
 * EMAIL FIRST, and that ordering is forced rather than fashionable. There is
 * one Cognito identity provider per CUSTOMER DIRECTORY — Cognito validates the
 * id_token's issuer, and Microsoft Entra issues a per-tenant one even for a
 * multi-tenant app registration — so "Continue with Microsoft" has to know
 * which organisation before it can redirect anywhere. The address is the only
 * thing the person has that says.
 *
 * The resolution happens SERVER-SIDE. The browser is never handed the list of
 * configured domains, because that list is the customer list.
 *
 * SSO is still the primary action and the password form the secondary: every
 * real customer arrives through their identity provider, and the password form
 * is for support and for the trial.
 *
 * The error message never distinguishes "no such account" from "wrong
 * password": that difference is an account-enumeration oracle.
 */
export function LoginPage() {
  const t = useT()
  const navigate = useNavigate()
  const location = useLocation()
  const signIn = useSessionStore((s) => s.signIn)
  // Prefilled ONLY in mock mode. The email is now the first thing a real
  // customer types and the thing that picks their identity provider, so a
  // demo address sitting in it is a person one careless click away from
  // "single sign-on is not set up for example.com".
  const [email, setEmail] = useState(USE_MOCKS ? 'owner@example.com' : '')
  const [password, setPassword] = useState(USE_MOCKS ? 'demo' : '')
  const from = (location.state as { from?: string } | null)?.from ?? '/'

  // A code, never the provider's own message: the callback is reachable by
  // anyone with a link, so the API refuses to echo attacker-supplied text onto
  // this page. The real reason is in the API's log.
  const [params] = useSearchParams()
  const ssoError = params.get('ssoError')
  const looksLikeEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())

  const login = useMutation({
    mutationFn: () => api.post<{ token: string; user: User; tenant: Tenant }>('/auth/login', { email, password }),
    onSuccess: (result) => {
      signIn(result)
      navigate(from, { replace: true })
    },
  })

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-fg">{t('login.title')}</h1>
      <p className="mt-1.5 text-base text-fg-muted">
        {t('login.subtitle')}
      </p>

      {ssoError && (
        <Alert tone="danger" title={t('login.ssoErrorTitle')} className="mt-6">
          {ssoError === 'directory_not_configured'
            ? t('login.ssoError.directoryNotConfigured')
            : t('login.ssoError.generic')}
        </Alert>
      )}

      <div className="mt-6 space-y-4">
        <Input
          label={t('login.workEmail')}
          type="email"
          autoComplete="email"
          required
          leftIcon={<Mail />}
          hint={t('login.workEmailHint')}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <div className="space-y-2">
          <Button
            fullWidth
            size="lg"
            disabled={!looksLikeEmail}
            leftIcon={<KeyRound className="h-4 w-4" />}
            onClick={() => beginSso('microsoft', from, email.trim())}
          >
            {t('login.continueMicrosoft')}
          </Button>
          <Button
            fullWidth
            size="lg"
            disabled={!looksLikeEmail}
            leftIcon={<KeyRound className="h-4 w-4" />}
            onClick={() => beginSso('google', from, email.trim())}
          >
            {t('login.continueGoogle')}
          </Button>
        </div>
      </div>

      <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wide text-fg-subtle">
        <span className="h-px flex-1 bg-border" />
        {t('common.or')}
        <span className="h-px flex-1 bg-border" />
      </div>

      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          login.mutate()
        }}
      >
        {login.error && (
          <Alert tone="danger" title={t('login.ssoErrorTitle')}>
            {login.error instanceof ApiError
              ? t('login.passwordCheck')
              : t('login.genericError')}
          </Alert>
        )}
        <Input
          label={t('login.password')}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={!looksLikeEmail}
          isLoading={login.isPending}
        >
          {t('login.signInWithPassword')}
        </Button>
      </form>
    </div>
  )
}
